import { FormEvent, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { PageHeader } from '../../components/layout/page/PageHeader';
import { PageShell } from '../../components/layout/page/PageShell';
import { CollapsibleFormSection } from '../../components/ui/CollapsibleFormSection';
import { DeleteIconButton } from '../../components/ui/DeleteIconButton';
import { EditIconButton } from '../../components/ui/EditIconButton';
import { FormActionBar } from '../../components/ui/FormActionBar';
import { ListPaginationBar } from '../../components/ui/ListPaginationBar';
import { useConfirmDialog } from '../../contexts/ConfirmDialogContext';
import { usePermissions } from '../../contexts/PermissionsContext';
import { useCollapsibleItemForm } from '../../hooks/useCollapsibleItemForm';
import {
  TechnologyAccessKey,
  TechnologyAccessKeyUpsertPayload,
  createTechnologyAccessKey,
  deleteTechnologyAccessKey,
  listTechnologyAccessKeys,
  updateTechnologyAccessKey,
} from '../../services/technologyApi';
import {
  TECHNOLOGY_ACCESS_KEY_KIND_OPTIONS,
  TECHNOLOGY_ACCESS_KEY_STATUS_OPTIONS,
  technologyOptionLabel,
} from './technologyFormOptions';

const PAGE_SIZE = 10;

const emptyForm: TechnologyAccessKeyUpsertPayload = {
  name: '',
  kind: 'Ssh',
  status: 'Active',
  fingerprint: '',
  publicKey: '',
  scope: '',
  lastRotatedAtUtc: null,
  expiresAtUtc: null,
  notes: '',
};

function toDateInputValue(value?: string | null): string {
  if (!value) {
    return '';
  }

  return value.slice(0, 10);
}

function fromDateInputValue(value: string): string | null {
  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }

  return `${trimmed}T00:00:00.000Z`;
}

function formatDate(value?: string | null): string {
  if (!value) {
    return '—';
  }

  return new Date(value).toLocaleDateString('pt-BR');
}

export function TechnologyKeysPage() {
  const { canWriteModule } = usePermissions();
  const { confirm } = useConfirmDialog();
  const canWrite = canWriteModule('technology.keys');
  const [items, setItems] = useState<TechnologyAccessKey[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [kindFilter, setKindFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<TechnologyAccessKeyUpsertPayload>(emptyForm);
  const [saving, setSaving] = useState(false);
  const { formExpanded, expandForm, collapseForm, handleCreateSuccess, toggleOrCancel } =
    useCollapsibleItemForm();
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  async function loadData(targetPage = page): Promise<void> {
    setLoading(true);
    try {
      const result = await listTechnologyAccessKeys({
        page: targetPage,
        pageSize: PAGE_SIZE,
        search: search.trim() || undefined,
        kind: kindFilter || undefined,
        status: statusFilter || undefined,
      });
      setItems(result.items);
      setTotalCount(result.totalCount);
      setPage(result.page);
    } catch {
      toast.error('Falha ao carregar chaves.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void loadData(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, kindFilter, statusFilter]);

  function resetForm(): void {
    setEditingId(null);
    setForm(emptyForm);
  }

  function startEdit(item: TechnologyAccessKey): void {
    expandForm();
    setEditingId(item.id);
    setForm({
      name: item.name,
      kind: item.kind,
      status: item.status,
      fingerprint: item.fingerprint ?? '',
      publicKey: item.publicKey ?? '',
      scope: item.scope ?? '',
      lastRotatedAtUtc: item.lastRotatedAtUtc ?? null,
      expiresAtUtc: item.expiresAtUtc ?? null,
      notes: item.notes ?? '',
    });
  }

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!canWrite) return;

    const payload: TechnologyAccessKeyUpsertPayload = {
      ...form,
      fingerprint: form.fingerprint?.trim() || null,
      publicKey: form.publicKey?.trim() || null,
      scope: form.scope?.trim() || null,
      lastRotatedAtUtc: form.lastRotatedAtUtc || null,
      expiresAtUtc: form.expiresAtUtc || null,
      notes: form.notes?.trim() || null,
    };

    setSaving(true);
    try {
      if (editingId) {
        await updateTechnologyAccessKey(editingId, payload);
        toast.success('Chave atualizada.');
        collapseForm(resetForm);
      } else {
        await createTechnologyAccessKey(payload);
        toast.success('Chave cadastrada.');
        handleCreateSuccess(resetForm);
      }
      await loadData(page);
    } catch {
      toast.error('Falha ao salvar chave.');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(item: TechnologyAccessKey): Promise<void> {
    if (!canWrite) return;
    const accepted = await confirm({
      title: 'Excluir chave',
      message: `Deseja excluir "${item.name}" do inventário de chaves?`,
      confirmLabel: 'Excluir',
      tone: 'danger',
    });
    if (!accepted) return;

    try {
      await deleteTechnologyAccessKey(item.id);
      toast.success('Chave excluída.');
      if (editingId === item.id) collapseForm(resetForm);
      await loadData(page);
    } catch {
      toast.error('Falha ao excluir chave.');
    }
  }

  return (
    <PageShell>
      <PageHeader
        title="Chaves SSH/API"
        description="Inventário de chaves de acesso e identidade. O material secreto fica no Cofre."
      />

      {canWrite ? (
        <CollapsibleFormSection
          title={editingId ? 'Editar chave' : 'Nova chave'}
          expanded={formExpanded}
          onToggle={() => toggleOrCancel(resetForm)}
          editingSummary={
            editingId
              ? `Editando ${form.name || 'chave selecionada'} - clique para expandir`
              : undefined
          }
          createSummary="Clique para expandir e cadastrar uma chave"
          isEditing={!!editingId}
        >
          <form onSubmit={(event) => void handleSubmit(event)} className="erp-form px-4 pb-4 pt-3">
            <p className="mb-3 text-xs text-secondary-500">
              Cadastre só metadados (fingerprint, chave pública, escopo). Guarde o material privado no{' '}
              <Link to="/technology/secrets" className="underline">
                Cofre
              </Link>
              .
            </p>
            <div className="erp-form-grid">
              <label className="erp-label">
                Nome
                <input
                  className="erp-input"
                  value={form.name}
                  onChange={(event) => setForm({ ...form, name: event.target.value })}
                  required
                />
              </label>
              <label className="erp-label">
                Tipo
                <select
                  className="erp-input"
                  value={form.kind}
                  onChange={(event) => setForm({ ...form, kind: event.target.value })}
                >
                  {TECHNOLOGY_ACCESS_KEY_KIND_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="erp-label">
                Status
                <select
                  className="erp-input"
                  value={form.status}
                  onChange={(event) => setForm({ ...form, status: event.target.value })}
                >
                  {TECHNOLOGY_ACCESS_KEY_STATUS_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="erp-label">
                Fingerprint / ID
                <input
                  className="erp-input"
                  value={form.fingerprint ?? ''}
                  onChange={(event) => setForm({ ...form, fingerprint: event.target.value })}
                />
              </label>
              <label className="erp-label sm:col-span-2">
                Escopo de uso
                <input
                  className="erp-input"
                  placeholder="Ex.: servidores prod, repositório X, integração Y"
                  value={form.scope ?? ''}
                  onChange={(event) => setForm({ ...form, scope: event.target.value })}
                />
              </label>
              <label className="erp-label">
                Última rotação
                <input
                  className="erp-input"
                  type="date"
                  value={toDateInputValue(form.lastRotatedAtUtc)}
                  onChange={(event) =>
                    setForm({ ...form, lastRotatedAtUtc: fromDateInputValue(event.target.value) })
                  }
                />
              </label>
              <label className="erp-label">
                Expira em
                <input
                  className="erp-input"
                  type="date"
                  value={toDateInputValue(form.expiresAtUtc)}
                  onChange={(event) =>
                    setForm({ ...form, expiresAtUtc: fromDateInputValue(event.target.value) })
                  }
                />
              </label>
            </div>
            <label className="erp-label mt-3 block">
              Chave pública (opcional)
              <textarea
                className="erp-input mt-1 min-h-[80px] font-mono text-xs"
                placeholder="ssh-ed25519 AAAA... comentário"
                value={form.publicKey ?? ''}
                onChange={(event) => setForm({ ...form, publicKey: event.target.value })}
              />
            </label>
            <label className="erp-label mt-3 block">
              Observações
              <textarea
                className="erp-input mt-1 min-h-[60px]"
                value={form.notes ?? ''}
                onChange={(event) => setForm({ ...form, notes: event.target.value })}
              />
            </label>
            <FormActionBar
              onCancel={() => collapseForm(resetForm)}
              cancelLabel="Cancelar"
              className="erp-btn-actions mt-4"
            >
              <button type="submit" disabled={saving} className="erp-btn-primary">
                {saving ? 'Salvando...' : editingId ? 'Atualizar' : 'Criar'}
              </button>
            </FormActionBar>
          </form>
        </CollapsibleFormSection>
      ) : null}

      <div className="mb-3 flex flex-wrap gap-2">
        <input
          className="erp-input max-w-xs"
          placeholder="Buscar por nome, fingerprint ou escopo"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              setPage(1);
              void loadData(1);
            }
          }}
        />
        <select
          className="erp-input max-w-[200px]"
          value={kindFilter}
          onChange={(event) => {
            setPage(1);
            setKindFilter(event.target.value);
          }}
        >
          <option value="">Todos os tipos</option>
          {TECHNOLOGY_ACCESS_KEY_KIND_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        <select
          className="erp-input max-w-[220px]"
          value={statusFilter}
          onChange={(event) => {
            setPage(1);
            setStatusFilter(event.target.value);
          }}
        >
          <option value="">Todos os status</option>
          {TECHNOLOGY_ACCESS_KEY_STATUS_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        <button
          type="button"
          className="erp-btn-secondary"
          onClick={() => {
            setPage(1);
            void loadData(1);
          }}
        >
          Buscar
        </button>
      </div>

      {loading ? (
        <p className="text-secondary-500">Carregando...</p>
      ) : (
        <>
          <div className="overflow-x-auto rounded-xl border border-secondary-200 bg-white">
            <table className="erp-table">
              <thead>
                <tr>
                  <th>Nome</th>
                  <th>Tipo</th>
                  <th>Status</th>
                  <th>Fingerprint</th>
                  <th>Escopo</th>
                  <th>Rotação</th>
                  <th>Expira</th>
                  {canWrite ? <th className="w-24">Ações</th> : null}
                </tr>
              </thead>
              <tbody>
                {items.length === 0 ? (
                  <tr>
                    <td colSpan={canWrite ? 8 : 7} className="text-center text-secondary-500">
                      Nenhuma chave cadastrada.
                    </td>
                  </tr>
                ) : (
                  items.map((item) => (
                    <tr key={item.id}>
                      <td>{item.name}</td>
                      <td>{technologyOptionLabel(TECHNOLOGY_ACCESS_KEY_KIND_OPTIONS, item.kind)}</td>
                      <td>
                        {technologyOptionLabel(TECHNOLOGY_ACCESS_KEY_STATUS_OPTIONS, item.status)}
                      </td>
                      <td className="max-w-[180px] truncate font-mono text-xs" title={item.fingerprint ?? undefined}>
                        {item.fingerprint || '—'}
                      </td>
                      <td className="max-w-[200px] truncate" title={item.scope ?? undefined}>
                        {item.scope || '—'}
                      </td>
                      <td>{formatDate(item.lastRotatedAtUtc)}</td>
                      <td>{formatDate(item.expiresAtUtc)}</td>
                      {canWrite ? (
                        <td>
                          <div className="flex gap-1">
                            <EditIconButton onClick={() => startEdit(item)} label="Editar" />
                            <DeleteIconButton
                              onClick={() => void handleDelete(item)}
                              label="Excluir"
                            />
                          </div>
                        </td>
                      ) : null}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {totalCount > PAGE_SIZE ? (
            <ListPaginationBar
              totalCount={totalCount}
              page={page}
              pageSize={PAGE_SIZE}
              onPrevious={() => setPage((current) => Math.max(1, current - 1))}
              onNext={() => setPage((current) => Math.min(totalPages, current + 1))}
            />
          ) : null}
        </>
      )}
    </PageShell>
  );
}
